import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { get, post } from '../lib/api.js';
import { formatBRL, formatDateTime, parseCentsInput, todayLocal, vehicleLabel } from '../lib/format.js';
import { Alert, Card, ErrorBox, Field, PageHeader, useSubmit } from '../components/ui.js';

/**
 * Registrar serviço concluído com retorno programado — a tela do minuto
 * (seção 2). Tudo acontece aqui: cliente, veículo, itens, hodômetro e a
 * confirmação da sequência de retorno.
 */

interface Customer {
  id: string;
  name: string;
  phone_e164: string | null;
}
interface Vehicle {
  id: string;
  plate: string | null;
  make: string | null;
  model: string | null;
  model_year: number | null;
}
interface CatalogItem {
  id: string;
  name: string;
  sku: string;
  unit: string;
  type: string;
}
interface Template {
  id: string;
  name: string;
  purpose: string;
  status: string;
  first_offset_amount: number;
  first_offset_unit: string;
}
interface Linha {
  itemId: string;
  name: string;
  unit: string;
  quantity: string;
  price: string;
}

const UNIDADE_LABEL: Record<string, string> = {
  days: 'dias',
  weeks: 'semanas',
  months: 'meses',
  years: 'anos',
};

export function RecordService() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [customerId, setCustomerId] = useState(params.get('cliente') ?? '');
  const [customerTerm, setCustomerTerm] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [performedOn, setPerformedOn] = useState(todayLocal());
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes] = useState('');
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [buscaItem, setBuscaItem] = useState('');
  const [programarRetorno, setProgramarRetorno] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [resultado, setResultado] = useState<{ firstSendAt: string | null; motivo?: string } | null>(null);

  const clientes = useResource<{ customers: Customer[] }>(
    `/api/customers?term=${encodeURIComponent(customerTerm)}`,
    [customerTerm],
  );
  const veiculos = useResource<{ vehicles: Vehicle[] }>(
    customerId ? `/api/customers/${customerId}/vehicles` : null,
  );
  const itens = useResource<{ items: CatalogItem[] }>(
    `/api/catalog/search?term=${encodeURIComponent(buscaItem)}&limit=20`,
    [buscaItem],
  );
  const templates = useResource<{ templates: Template[] }>('/api/reminder-templates');
  const ativos = useMemo(
    () => (templates.data?.templates ?? []).filter((t) => t.status === 'active'),
    [templates.data],
  );

  useEffect(() => {
    if (!templateId && ativos.length > 0) setTemplateId(ativos[0]!.id);
  }, [ativos, templateId]);

  const template = ativos.find((t) => t.id === templateId) ?? null;

  const total = linhas.reduce((acc, linha) => {
    const cents = parseCentsInput(linha.price) ?? 0;
    const qty = Number(linha.quantity.replace(',', '.')) || 0;
    return acc + Math.round(cents * qty);
  }, 0);

  async function adicionarItem(item: CatalogItem) {
    let preco = '';
    try {
      const quote = await post<{ quote: { lines: { unitPriceCents: number }[] } }>('/api/quotes', {
        lines: [{ itemId: item.id, quantity: '1' }],
      });
      const cents = quote.quote.lines[0]?.unitPriceCents ?? 0;
      preco = (cents / 100).toFixed(2).replace('.', ',');
    } catch {
      // Item sem preço cadastrado entra com valor em branco: sob consulta.
    }
    setLinhas((atual) => [
      ...atual,
      { itemId: item.id, name: item.name, unit: item.unit, quantity: '1', price: preco },
    ]);
    setBuscaItem('');
  }

  const { submit, saving, error } = useSubmit(async () => {
    if (!customerId) throw new Error('Escolha o cliente.');
    if (linhas.length === 0) throw new Error('Adicione ao menos um item ou serviço realizado.');

    const response = await post<{
      service: { id: string };
      cycle: { cycleId: string | null; firstSend: { effectiveAt: string } | null; reason?: string } | null;
    }>('/api/services', {
      customerId,
      vehicleId: vehicleId || null,
      purpose: template?.purpose ?? 'general',
      performedOn,
      odometerKm: odometer ? Number(odometer) : null,
      notes: notes || null,
      items: linhas.map((linha) => ({
        itemId: linha.itemId,
        quantity: linha.quantity.replace(',', '.'),
        unitPriceCents: parseCentsInput(linha.price),
      })),
      createReturnCycle: programarRetorno,
      templateId: programarRetorno && templateId ? templateId : undefined,
    });

    setResultado({
      firstSendAt: response.cycle?.firstSend?.effectiveAt ?? null,
      motivo: response.cycle?.reason,
    });
  });

  if (resultado) {
    return (
      <>
        <PageHeader title="Serviço registrado" />
        <Card>
          {resultado.firstSendAt ? (
            <Alert tone="info" title={`Primeiro contato de retorno em ${formatDateTime(resultado.firstSendAt)}`}>
              O contato só sai se o cliente tiver consentimento registrado e nenhuma resposta ou
              reserva tiver alterado o ciclo até lá.
            </Alert>
          ) : (
            <Alert tone="warning" title="Serviço registrado sem retorno programado">
              {resultado.motivo === 'no_template'
                ? 'Não há sequência de retorno ativa para esta finalidade. Crie uma em Lembretes e retornos.'
                : 'O retorno não foi programado nesta operação.'}
            </Alert>
          )}
          <div className="row">
            <button
              type="button"
              className="primary"
              onClick={() => {
                setResultado(null);
                setLinhas([]);
                setOdometer('');
                setNotes('');
              }}
            >
              Registrar outro serviço
            </button>
            <button type="button" onClick={() => navigate('/')}>
              Voltar para a visão geral
            </button>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Registrar serviço"
        description="Uma tela: cliente, veículo, itens e a confirmação do retorno."
      />

      <form onSubmit={submit}>
        <ErrorBox error={error} />

        <div className="grid cols-2">
          <Card title="Cliente e veículo">
            <Field label="Buscar cliente">
              <input
                type="text"
                value={customerTerm}
                onChange={(e) => setCustomerTerm(e.target.value)}
                placeholder="Nome ou telefone"
              />
            </Field>
            <Field label="Cliente" required>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required>
                <option value="">Escolha o cliente</option>
                {(clientes.data?.customers ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Veículo" hint="Opcional, mas necessário para o retorno citar o veículo.">
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">Sem veículo</option>
                {(veiculos.data?.vehicles ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {vehicleLabel(v)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="field-row">
              <Field label="Data do serviço" required>
                <input
                  type="date"
                  value={performedOn}
                  max={todayLocal()}
                  onChange={(e) => setPerformedOn(e.target.value)}
                  required
                />
              </Field>
              <Field label="Hodômetro (km)">
                <input type="number" min={0} value={odometer} onChange={(e) => setOdometer(e.target.value)} />
              </Field>
            </div>
          </Card>

          <Card title="Retorno programado">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={programarRetorno}
                onChange={(e) => setProgramarRetorno(e.target.checked)}
              />
              Programar retorno para este serviço
            </label>

            {programarRetorno ? (
              ativos.length === 0 ? (
                <Alert tone="warning" title="Nenhuma sequência ativa">
                  Crie e publique uma sequência em Lembretes e retornos para programar o retorno.
                </Alert>
              ) : (
                <>
                  <Field label="Sequência">
                    <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                      {ativos.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {template ? (
                    <p className="hint">
                      Primeiro contato {template.first_offset_amount}{' '}
                      {UNIDADE_LABEL[template.first_offset_unit] ?? template.first_offset_unit} após a
                      data do serviço, dentro da janela de envio da loja. Meses são de calendário.
                    </p>
                  ) : null}
                </>
              )
            ) : (
              <p className="hint">
                Sem retorno programado, o serviço fica só no histórico.
              </p>
            )}

            <Field label="Observações">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </Card>
        </div>

        <Card
          title="Itens e serviços realizados"
          description="Os valores vêm do preço vigente e podem ser ajustados para o que foi efetivamente cobrado."
        >
          <Field label="Adicionar item">
            <input
              type="text"
              value={buscaItem}
              onChange={(e) => setBuscaItem(e.target.value)}
              placeholder="Buscar no catálogo"
            />
          </Field>

          {buscaItem && itens.data?.items.length ? (
            <ul className="list" style={{ border: '1px solid var(--gl-border)', borderRadius: 6, marginBottom: 14 }}>
              {itens.data.items.slice(0, 6).map((item) => (
                <li key={item.id}>
                  <div className="list-main">
                    <div className="list-title">{item.name}</div>
                    <div className="list-sub">{item.sku}</div>
                  </div>
                  <button type="button" className="small" onClick={() => void adicionarItem(item)}>
                    Adicionar
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {linhas.length === 0 ? (
            <p className="muted small">Nenhum item adicionado ainda.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th style={{ width: 120 }}>Quantidade</th>
                    <th style={{ width: 160 }}>Valor unitário</th>
                    <th className="num">Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((linha, index) => {
                    const cents = parseCentsInput(linha.price) ?? 0;
                    const qty = Number(linha.quantity.replace(',', '.')) || 0;
                    return (
                      <tr key={`${linha.itemId}-${index}`}>
                        <td>
                          {linha.name}
                          <div className="small muted">{linha.unit}</div>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={linha.quantity}
                            inputMode="decimal"
                            onChange={(e) =>
                              setLinhas((atual) =>
                                atual.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={linha.price}
                            inputMode="decimal"
                            placeholder="sob consulta"
                            onChange={(e) =>
                              setLinhas((atual) =>
                                atual.map((l, i) => (i === index ? { ...l, price: e.target.value } : l)),
                              )
                            }
                          />
                        </td>
                        <td className="num">{formatBRL(Math.round(cents * qty))}</td>
                        <td className="actions">
                          <button
                            type="button"
                            className="small danger"
                            onClick={() => setLinhas((atual) => atual.filter((_, i) => i !== index))}
                          >
                            Remover
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="row" style={{ marginTop: 16 }}>
            <strong>Total do serviço: {formatBRL(total)}</strong>
            <span className="right">
              <button type="submit" className="primary" disabled={saving}>
                {saving ? 'Registrando…' : 'Registrar serviço'}
              </button>
            </span>
          </div>
        </Card>
      </form>
    </>
  );
}

export { get };
